"use client";

import BlogPreviewTile, { BlogPreviewPost } from "../BlogPreviewTile";
import { useEffect, useLayoutEffect, useRef } from "react";

import { FadeInSection } from "../FadeInSection";
import HomepageAboutSection from "../HomepageAboutSection";
import { Icons } from "../icons/icons";
import MainFooter from "../MainFooter";
import StoneIcon from "../StoneIcon";
import { WPAPIPostGraphQL } from "@/types";
import { cn } from "@/lib/utils";
import { useDataContext } from "../../app/_context";

export default function InfoPage({
  posts,
  blogPost,
  blogExcerpt,
}: {
  posts: WPAPIPostGraphQL[];
  blogPost: BlogPreviewPost;
  blogExcerpt: string | null;
}) {
  const { setScrollPositionY, setViewportHeight } = useDataContext();
  const mainContainerRef = useRef<HTMLElement>(null);

  // Event listener to track scroll position
  useLayoutEffect(() => {
    const container = mainContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setScrollPositionY(container.scrollTop);
    };

    handleScroll();
    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [setScrollPositionY]);

  // Event listener to track viewport height
  useEffect(() => {
    if (typeof window !== "undefined") {
      const updateViewportHeight = () => {
        setViewportHeight(window.innerHeight);
      };

      // Set the initial height
      updateViewportHeight();

      // Add resize event listener
      window.addEventListener("resize", updateViewportHeight);

      // Cleanup event listener when component unmounts
      return () => {
        window.removeEventListener("resize", updateViewportHeight);
      };
    }
  }, [setViewportHeight]);

  return posts ? (
    <main
      className={cn(
        "absolute inset-0 flex flex-col overflow-y-auto overflow-x-clip bg-background text-foreground",
      )}
      ref={mainContainerRef}
    >
      <div className="flex flex-col items-center">
        {/* <CookieModal /> */}
        <FadeInSection
          as="section"
          className="relative w-full border-b border-foreground/10"
        >
          {/* Hero gradient blobs */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 80% -5%, hsl(249 53% 55% / 0.09) 0%, transparent 58%)",
            }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 10% 110%, hsl(84 70% 55% / 0.07) 0%, transparent 52%)",
            }}
          />
          <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 pt-16 pb-10 text-center sm:px-6 md:py-14 lg:min-h-[calc(100dvh-11rem)] lg:px-8 lg:justify-center">
            <StoneIcon className="mt-5 w-32 md:w-12" />
            <h1 className="mt-4 font-PlayfairDisplay text-5xl font-semibold leading-none text-foreground md:text-6xl lg:text-7xl">
              We make digital non-fiction that holds up.
            </h1>
            <p className="mt-6 max-w-2xl font-Cabin text-lg leading-relaxed text-foreground/80 dark:text-foreground/85 md:text-xl">
              Research, editorial, content, social media and web design —
              across every form non-fiction takes online.
            </p>

            <ul className="mt-6 flex max-w-2xl flex-wrap justify-center gap-2">
              {[
                "Investigations",
                "Explainers",
                "Campaign pages",
                "Briefing sites",
                "Reels",
                "Threads",
                "Newsletters",
                "Scripts",
                "Captions",
              ].map((label) => (
                <li
                  key={label}
                  className="rounded-sm border border-foreground/20 px-2.5 py-1 font-Cabin text-lg text-foreground/70 dark:text-foreground/75"
                >
                  {label}
                </li>
              ))}
            </ul>

            <p className="mt-8 font-Cabin text-lg leading-relaxed text-foreground/60 dark:text-foreground/65">
              Long or short. Short can be well-researched too — and often has to be.
            </p>
          </div>
        </FadeInSection>

        <FadeInSection delay={100}>
          <HomepageAboutSection />
        </FadeInSection>

        <FadeInSection
          as="section"
          delay={100}
          className="w-full border-t border-foreground/10 px-4 py-16 sm:px-6 md:py-20 lg:px-8"
        >
          <div className="mx-auto grid w-full max-w-7xl gap-8 lg:grid-cols-[0.35fr_0.65fr] lg:items-start">
            <h2 className="font-PlayfairDisplay text-3xl font-semibold md:text-4xl">
              From the Blog
            </h2>
            <BlogPreviewTile
              post={blogPost}
              excerpt={blogExcerpt}
              animationTrigger={true}
            />
          </div>
        </FadeInSection>

        <MainFooter />
      </div>
    </main>
  ) : (
    <div className="w-full py-20 flex place-items-center text-lime-200 h-screen">
      <Icons.spinner className="animate-spin h-8 w-8" />
    </div>
  );
}
